import { type WritableDraft, produce } from 'immer';
import type { OpenAPIV3 } from 'openapi-types';

import type { OasOperationTable } from '../../oas/OasOperationTable';
import {
  existsFieldInOASRequestBody,
  getTableSnapshotMode,
} from '../../oas/helpers';
import {
  type OasTableColumn,
  type OasTableColumnWithLink,
  isOasTableColumnAsn,
  isOasTableColumnBgpRouting,
  isOasTableColumnBoolean,
  isOasTableColumnCompactNumber,
  isOasTableColumnDateTime,
  isOasTableColumnEnum,
  isOasTableColumnHostname,
  isOasTableColumnHostnameObject,
  isOasTableColumnInterface,
  isOasTableColumnLabels,
  isOasTableColumnMplsNextHop,
  isOasTableColumnNeighborIp,
  isOasTableColumnNtpSummarySources,
  isOasTableColumnNumber,
  isOasTableColumnObject,
  isOasTableColumnSecurity,
  isOasTableColumnSwitchportIntName,
  isOasTableColumnText,
  isOasTableColumnUptime,
  isOasTableColumnUrl,
} from '../../oas/index';
import { isNonNullable, safeAbsurd } from '../../types/index';
import { formatStringTemplate, omitUndefined } from '../../utils/index';
import type { TableColumn, TableEndpoint } from '../TableEndpoint';
import {
  createAsnColumn,
  createBooleanColumn,
  createCompactNumberColumn,
  createDateTimeColumn,
  createEnumColumn,
  createHostnameColumn,
  createInterfaceColumn,
  createLabelsColumn,
  createMplsNextHopColumn,
  createNeighborIpColumn,
  createNtpSummarySourcesColumn,
  createNumberColumn,
  createObjectColumn,
  createSecurityColumn,
  createSwitchportIntNameColumn,
  createTextColumn,
  createUptimeColumn,
} from '../columns/index';
import { createBgpRoutingColumn } from '../columns/types/bgpRoutingColumn';
import { createHostnameObjectColumn } from '../columns/types/hostnameObjectColumn';
import { createUrlColumn } from '../columns/types/urlColumn';
import {
  type TableFilterOperator,
  isLabelsTableFilter,
  isObjectTableFilter,
} from '../filters/TableFilter';
import { createLabelsFilter } from '../filters/types/labelsFilter';
import { createTableColumnFilterFromOas } from './createTableColumnFilterFromOas';
import { createTableColumnLinkFromOas } from './createTableColumnLinkFromOas';
import {
  type ColumnFilterSupport,
  type OasFilters,
  getTableFilterSupport,
} from './getTableFilterSupport';

/** Attributes common to all columns including special ones like 'hostname' */
const pickColumnCommon = (column: OasTableColumn) =>
  omitUndefined({
    key: column.key,
    title: column.title ?? column.key,
    description: column.description,
    hidden: column.hidden,
    width: column.width,
    // return filter value if falsy since it can be `undefined`
    // for default filter or `false` for no filter
    filter: column.filter
      ? createTableColumnFilterFromOas(column.filter)
      : column.filter,
    // we need to override column factory data check on this field
    // since we do not know table row TS type when creating tables
    // from OAS
    array: column.array as any,
    disabledSort: column.disabledSort,
    disabledColorize: column.disabledColorize,
    renderContentNamed: column.renderContent,
  });

/** Attributes common to basic columns (text and number) */
const pickColumnLink = (
  column: OasTableColumnWithLink & { key: string },
  isNewCloudModel?: boolean,
) =>
  omitUndefined({
    link: column.link
      ? createTableColumnLinkFromOas(column.link, column.key, isNewCloudModel)
      : undefined,
  });

const getOasFiltersFromOasOperationTable = (
  oasOperationTable: OasOperationTable,
): OasFilters | undefined =>
  (
    (oasOperationTable.requestBody as OpenAPIV3.RequestBodyObject)?.content?.[
      'application/json'
    ]?.schema as OpenAPIV3.SchemaObject
  )?.properties?.filters as OasFilters | undefined;

/**
 * List of known operators that are not supported by frontend or handled differently -> not included in operator list.
 *
 * When a new operator appears, frontend starts stumbling on missing locale. If we don't intend
 * to implement it, just add it there.
 */
const knownUnsupportedOperators = ['empty'];

const ARRAY_CONTAINS_OPERATORS = ['containsAny', 'containsAll'] as const;

const getArrayContainsOperators = (
  filterSupport: ColumnFilterSupport,
): TableFilterOperator[] =>
  filterSupport.operators.filter((operator): operator is TableFilterOperator =>
    (ARRAY_CONTAINS_OPERATORS as readonly string[]).includes(operator),
  );

const getOperatorsFromColumnFilterSupport = (
  filterSupport: ColumnFilterSupport,
): TableFilterOperator[] => {
  const operators = filterSupport.operators.filter(
    (o) => !knownUnsupportedOperators.includes(o),
  ) as TableFilterOperator[];
  if (filterSupport.operators.includes('empty')) {
    operators.push('emptyTrue');
    operators.push('emptyFalse');
  }

  // since frontend now supports only (non-)equality cross-column operations and handles them specifically,
  // we need to add those frontend operators to the list of operators.
  if (filterSupport.crossColumnOperators.includes('eq')) {
    operators.push('column');
  }
  if (filterSupport.crossColumnOperators.includes('neq')) {
    operators.push('ncolumn');
  }

  return operators;
};

const createTableColumnFromOas = (
  oasColumn: OasTableColumn,
  filterSupport?: ColumnFilterSupport,
  isNewCloudModel?: boolean,
): TableColumn<any> | null => {
  const fillObjectChildrenFiltersSupportedOperators = (
    draft: WritableDraft<TableColumn<any>>,
    objectChildFilterSupport:
      | {
          [p: string]: ColumnFilterSupport;
        }
      | undefined,
  ) => {
    if (isObjectTableFilter(draft.filter) && objectChildFilterSupport) {
      for (const child of draft.filter.children) {
        const childFilterSupport = objectChildFilterSupport[child.field];
        if (childFilterSupport) {
          child.filter.operators =
            getOperatorsFromColumnFilterSupport(childFilterSupport);
        }
      }
    }
  };

  const column = createTableColumnFromOasWithHardcodedOperators(
    oasColumn,
    isNewCloudModel,
  );
  if (
    // failed to initialize (just passthru null) or not having a filter to be processed
    !column?.filter ||
    // dynamic filter support data were not provided
    !filterSupport
  ) {
    return column;
  }

  return produce(column, (draft) => {
    const singleItemFilter = isObjectTableFilter(draft.filter)
      ? // With current implementation, we show only column-wide operators for object columns,
        // even when they contain array of objects; single item operators on values are used only for child filters.
        // For arrays of other types, we use operators from arrayItem as UI offers operations on an array item directly,
        // prefixed with select whether "any item" or "all items" must match.
        filterSupport
      : (filterSupport.arrayItem ?? filterSupport);
    const arrayContainsOperators = draft.array
      ? getArrayContainsOperators(filterSupport)
      : [];
    // Rewrite top level operators with those from oas.
    // Filter existence ensured by column?.filter check above.
    draft.filter!.operators = [
      ...getOperatorsFromColumnFilterSupport(singleItemFilter),
      ...arrayContainsOperators,
    ];
    draft.filter!.comparableColumns = singleItemFilter.comparableColumns;

    // Labels columns use a dedicated filter UX (includes / includes name / empty).
    // Replace rewritten item-level text operators with the labels operator set.
    if (isLabelsTableFilter(draft.filter)) {
      draft.filter.operators = createLabelsFilter().operators;
    }

    if (isObjectTableFilter(draft.filter)) {
      // see NIM-14722 for explanation and eventually remove if the operator is removed from OAS
      draft.filter.operators = draft.filter.operators.filter(
        (op) => op !== 'eq',
      );
    }
    // rewrite object children operators with those from oas
    const objectChildFilterSupport =
      filterSupport.arrayItem?.objectProperties ??
      filterSupport.objectProperties;

    fillObjectChildrenFiltersSupportedOperators(
      draft,
      objectChildFilterSupport,
    );
  });
};

/**
 * Originally, a set of operators was hardcoded on frontend side.
 * Now we crawl it from OAS. But current architecture of tables
 * was built on fixed filter types and would deserve to be re-thought
 * in wider contexts (see NIM-10828), and it may be beneficial to see
 * dynamic operator logic separated from original filter creation.
 *
 * So let's keep original logic for now, just wrap it by fetching operators from OAS.
 */
const createTableColumnFromOasWithHardcodedOperators = (
  column: OasTableColumn,
  isNewCloudModel?: boolean,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: switch for many column types
): TableColumn<any> | null => {
  if (isOasTableColumnText(column)) {
    return createTextColumn({
      ...pickColumnCommon(column),
      ...pickColumnLink(column, isNewCloudModel),
      multiline: column.multiline,
    });
  }
  if (isOasTableColumnNumber(column)) {
    return createNumberColumn({
      ...pickColumnCommon(column),
      ...pickColumnLink(column, isNewCloudModel),
      decimals: column.decimals,
    });
  }
  if (isOasTableColumnHostname(column)) {
    return createHostnameColumn({
      ...pickColumnCommon(column),
      snField: column.snField,
    });
  }
  if (isOasTableColumnBoolean(column)) {
    return createBooleanColumn({
      ...pickColumnCommon(column),
      ...pickColumnLink(column, isNewCloudModel),
      options: column.options,
    });
  }
  if (isOasTableColumnUptime(column)) {
    return createUptimeColumn({
      ...pickColumnCommon(column),
      ...pickColumnLink(column, isNewCloudModel),
    });
  }
  if (isOasTableColumnDateTime(column)) {
    return createDateTimeColumn({
      ...pickColumnCommon(column),
      format: column.format,
    });
  }
  if (isOasTableColumnCompactNumber(column)) {
    return createCompactNumberColumn({
      ...pickColumnCommon(column),
      ...pickColumnLink(column, isNewCloudModel),
    });
  }
  if (isOasTableColumnAsn(column)) {
    return createAsnColumn({
      ...pickColumnCommon(column),
      ...pickColumnLink(column, isNewCloudModel),
    });
  }
  if (isOasTableColumnEnum(column)) {
    return createEnumColumn({
      ...pickColumnCommon(column),
      options: column.options,
      multiSelectFilter: column.multiSelectFilter,
      optionKeyType: column.optionKeyType,
    });
  }
  if (isOasTableColumnUrl(column)) {
    return createUrlColumn({
      ...pickColumnCommon(column),
      label: column.label,
    });
  }
  if (isOasTableColumnSecurity(column)) {
    return createSecurityColumn({
      ...pickColumnCommon(column),
      hostSnField: column.hostSnField,
      ruleNameField: column.ruleNameField,
      highlightedRuleIndexField: column.highlightedRuleIndexField,
      securityType: column.securityType,
    });
  }
  if (isOasTableColumnMplsNextHop(column)) {
    return createMplsNextHopColumn({
      ...pickColumnCommon(column),
    });
  }
  if (isOasTableColumnInterface(column)) {
    return createInterfaceColumn({
      ...pickColumnCommon(column),
      snField: column.snField,
    });
  }
  if (isOasTableColumnLabels(column)) {
    return createLabelsColumn({
      ...pickColumnCommon(column),
      targetIdField: column.targetIdField,
      targetType: column.targetType,
    });
  }
  if (isOasTableColumnHostnameObject(column)) {
    return createHostnameObjectColumn({
      ...pickColumnCommon(column),
      hostnameProp: column.hostnameProp,
      snProp: column.snProp,
    });
  }
  if (isOasTableColumnObject(column)) {
    return createObjectColumn({
      ...pickColumnCommon(column),
      toWebString: (value) =>
        formatStringTemplate(
          column.toWebString,
          value,
          column.toWebStringTemplateFallback,
          column.toWebStringTemplateDelimiter,
        ),
    });
  }
  if (isOasTableColumnBgpRouting(column)) {
    return createBgpRoutingColumn({
      ...pickColumnCommon(column),
      toWebString: (value) =>
        formatStringTemplate(
          column.toWebString,
          value,
          column.toWebStringTemplateFallback,
          column.toWebStringTemplateDelimiter,
        ),
    });
  }
  if (isOasTableColumnNtpSummarySources(column)) {
    return createNtpSummarySourcesColumn({
      ...pickColumnCommon(column),
      toWebString: (value) =>
        formatStringTemplate(
          column.toWebString,
          value,
          column.toWebStringTemplateFallback,
        ),
    });
  }
  if (isOasTableColumnSwitchportIntName(column)) {
    return createSwitchportIntNameColumn({
      ...pickColumnCommon(column),
      ...pickColumnLink(column, isNewCloudModel),
      macCountField: column.macCountField,
    });
  }
  if (isOasTableColumnNeighborIp(column)) {
    return createNeighborIpColumn({
      ...pickColumnCommon(column),
      protocolField: column.protocolField,
    });
  }

  safeAbsurd(column, `Unsupported column type ${(column as any)?.type}`);
  return null;
};

export const createTableEndpointFromOas = (
  apiEndpoint: string,
  oasOperationTable: OasOperationTable,
  isNewCloudModel?: boolean, // Set from `ENABLE_NEW_CLOUD_MODEL`; temporary until the flag is removed and this branching goes with it.
): TableEndpoint<any> => {
  const oasTable = oasOperationTable['x-table'];
  if (!oasTable.title || !oasTable.columns) {
    throw new Error(
      `Cannot create table endpoint for ${apiEndpoint} without all required fields`,
    );
  }
  const deviceViewAnchor = oasTable.deviceViewAnchor;
  const webDependencies = deviceViewAnchor
    ? [
        ...new Set([
          ...(oasTable.webDependencies ?? []),
          deviceViewAnchor.snField,
          deviceViewAnchor.hostnameField,
        ]),
      ]
    : oasTable.webDependencies;
  return {
    apiEndpoint,
    deprecated: oasOperationTable.deprecated,
    description: oasTable.description,
    operationId: oasOperationTable.operationId,
    id: oasTable.webPath ?? `apiEndpoint#${apiEndpoint}`,
    title: oasTable.title,
    snapshot: getTableSnapshotMode(oasOperationTable),
    requireAttributeFilters: existsFieldInOASRequestBody(
      oasOperationTable,
      'attributeFilters',
    ),
    webDependencies,
    deviceViewAnchor,
    webPath: oasTable.webPath,
    columns: createColumnsFromOas(oasOperationTable, isNewCloudModel),
    deviceFilters: {
      enabled: oasTable.deviceFilters.enabled,
      properties: oasTable.deviceFilters.properties,
    },
  };
};

export const createColumnsFromOas = (
  oasOperationTable: OasOperationTable,
  isNewCloudModel?: boolean, // Set from `ENABLE_NEW_CLOUD_MODEL`; temporary until the flag is removed and this branching goes with it.
): TableColumn<any>[] => {
  const visibleColumns = (oasOperationTable['x-table'].columns ?? []).filter(
    (column) => !column.disabledUI,
  );

  const oasFilters = getOasFiltersFromOasOperationTable(oasOperationTable);

  const filters =
    oasFilters !== undefined
      ? getTableFilterSupport(oasFilters, visibleColumns)
      : undefined;

  return visibleColumns
    .map((column) =>
      createTableColumnFromOas(column, filters?.[column.key], isNewCloudModel),
    )
    .filter(isNonNullable);
};
